/** The signed-in person's tasks for a workspace (optionally one bot), kept
 * fresh while any is still moving. Each refresh asks the server for the task,
 * which also syncs it with its session, so progress shows even if the
 * webhook is not set up. Mount it with a key per workspace and bot: state is
 * not reset when they change. */
import { useCallback, useEffect, useRef, useState } from 'react';
import { botTasksService, isBotTaskActive, type BotTask } from '../../../services/BotTasksService';

const POLL_MS = 4_000;
/** Reopening a panel shows what it had at once instead of refetching; active
 * tasks are refreshed by the poll anyway. */
const LIST_TTL_MS = 30_000;
const listCache = new Map<string, { at: number; tasks: BotTask[] }>();
const cacheKey = (workspaceId: string, botId?: string) => `${workspaceId}|${botId ?? ''}`;
/** Refreshing more at once would just queue requests behind each other. */
const MAX_POLLED = 5;

/** `reloadKey` changing re-lists past the cache: a task can be started
 * elsewhere (QuantisAI's assign_bot_task), and polling only refreshes tasks
 * this list already knows. */
export function useBotTasks(workspaceId: string | null | undefined, botId?: string, reloadKey?: string | number) {
  const [fresh] = useState<BotTask[] | null>(() => {
    const cached = workspaceId ? listCache.get(cacheKey(workspaceId, botId)) : undefined;
    return cached && Date.now() - cached.at < LIST_TTL_MS ? cached.tasks : null;
  });
  const [tasks, setTasks] = useState<BotTask[]>(fresh ?? []);
  const [loaded, setLoaded] = useState(fresh !== null);
  const [error, setError] = useState<string | null>(null);
  const tasksRef = useRef(tasks);
  useEffect(() => {
    tasksRef.current = tasks;
    if (workspaceId && loaded) listCache.set(cacheKey(workspaceId, botId), { at: listCache.get(cacheKey(workspaceId, botId))?.at ?? Date.now(), tasks });
  }, [tasks, workspaceId, botId, loaded]);

  // Tasks started or changed while a list request is in flight: its response
  // predates them, so it must not replace them.
  const touched = useRef(new Set<string>());
  // When each task was last changed here, by an action or a start: a poll
  // asked before that must not put the older state back.
  const changedAt = useRef(new Map<string, number>());
  const upsert = useCallback((task: BotTask) => {
    touched.current.add(task.id);
    changedAt.current.set(task.id, Date.now());
    setTasks((previous) => {
      const others = previous.filter((item) => item.id !== task.id);
      return [task, ...others].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    });
  }, []);

  const firstLoad = useRef(true);
  useEffect(() => {
    if (!workspaceId) return;
    const hit = listCache.get(cacheKey(workspaceId, botId));
    const useCache = firstLoad.current;
    firstLoad.current = false;
    if (useCache && hit && Date.now() - hit.at < LIST_TTL_MS) return;
    let active = true;
    touched.current = new Set();
    botTasksService.list(workspaceId, botId)
      .then((list) => {
        listCache.set(cacheKey(workspaceId, botId), { at: Date.now(), tasks: list });
        if (!active) return;
        setTasks((previous) => {
          const newer = previous.filter((task) => touched.current.has(task.id));
          const ids = new Set(newer.map((task) => task.id));
          return [...newer, ...list.filter((task) => !ids.has(task.id))].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
        });
        setError(null);
      })
      .catch((err) => { if (active) setError(err instanceof Error ? err.message : 'Tasks could not be loaded.'); })
      .finally(() => { if (active) setLoaded(true); });
    return () => { active = false; };
  }, [workspaceId, botId, reloadKey]);

  const hasActive = tasks.some(isBotTaskActive);
  useEffect(() => {
    if (!hasActive) return;
    let cancelled = false;
    // Rotates through the active tasks, so newer ones (say, five waiting on
    // approval) never keep an older one from refreshing.
    let cursor = 0;
    const timer = setInterval(() => {
      const active = tasksRef.current.filter(isBotTaskActive);
      const due = active.length <= MAX_POLLED ? active
        : Array.from({ length: MAX_POLLED }, (_, i) => active[(cursor + i) % active.length]);
      cursor = (cursor + MAX_POLLED) % Math.max(active.length, 1);
      for (const task of due) {
        const askedAt = Date.now();
        botTasksService.get(task.id).then((fresh) => {
          if (cancelled || (changedAt.current.get(task.id) ?? 0) > askedAt) return;
          setTasks((previous) => previous.map((item) => (item.id === fresh.id ? fresh : item)));
        }).catch(() => {});
      }
    }, POLL_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, [hasActive, upsert]);

  return { tasks, loaded, error, upsert };
}
